import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  Camera,
  Loader2,
  X,
  Link as LinkIcon,
  UtensilsCrossed,
  Users,
  Search,
  Trash2,
  Star,
} from 'lucide-react'
import { PlaceAutocomplete } from '@/components/PlaceAutocomplete'
import { cn } from '@/lib/utils'
import { useContact, useCreateContact, useUpdateContact, useContacts } from '@/hooks/use-contacts'
import { useCircles } from '@/hooks/use-circles'
import { useLabels, useCreateLabel } from '@/hooks/use-labels'
import {
  useContactRelationships,
  useCreateContactRelationship,
  useUpdateContactRelationship,
  useDeleteContactRelationship,
} from '@/hooks/use-contact-relationships'
import type { ContactFrequency, RelationshipType } from '@/lib/types'

const RELATIONSHIP_TYPES: { value: RelationshipType; label: string; color: string }[] = [
  { value: 'friend', label: 'Friend', color: '#22c55e' },
  { value: 'family', label: 'Family', color: '#a855f7' },
  { value: 'colleague', label: 'Colleague', color: '#3b82f6' },
  { value: 'acquaintance', label: 'Acquaintance', color: '#94a3b8' },
  { value: 'introduced_by', label: 'Introduced by', color: '#f59e0b' },
]

const RELATIONSHIP_COLOR_MAP: Record<string, string> = Object.fromEntries(
  RELATIONSHIP_TYPES.map((t) => [t.value, t.color]),
)

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp'
const MAX_AVATAR_SIZE = 10 * 1024 * 1024 // 10MB

const FREQUENCIES: { value: ContactFrequency; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
]

const LINK_KEYS = [
  'website',
  'linkedin',
  'instagram',
  'tiktok',
  'x',
  'github',
  'youtube',
  'pinterest',
  'snapchat',
] as const
type LinkKey = (typeof LINK_KEYS)[number]

const LINK_LABELS: Record<LinkKey, string> = {
  website: 'Website',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X (Twitter)',
  github: 'GitHub',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
}

const LINK_PLACEHOLDERS: Record<LinkKey, string> = {
  website: 'https://example.com',
  linkedin: 'https://linkedin.com/in/username',
  instagram: 'https://instagram.com/username',
  tiktok: 'https://tiktok.com/@username',
  x: 'https://x.com/username',
  github: 'https://github.com/username',
  youtube: 'https://youtube.com/@username',
  pinterest: 'https://pinterest.com/username',
  snapchat: 'https://snapchat.com/add/username',
}

interface FormState {
  firstName: string
  lastName: string
  nickname: string
  email: string
  phone: string
  company: string
  jobTitle: string
  location: string
  birthday: string
  anniversary: string
  notes: string
  contactFrequency: ContactFrequency
  circleIds: string[]
  labelIds: string[]
  links: Record<string, string>
  foodDietary: string[]
  foodAllergies: string[]
  foodFavorites: string[]
  foodNotes: string
}

const INITIAL_STATE: FormState = {
  firstName: '',
  lastName: '',
  nickname: '',
  email: '',
  phone: '',
  company: '',
  jobTitle: '',
  location: '',
  birthday: '',
  anniversary: '',
  notes: '',
  contactFrequency: 'none',
  circleIds: [],
  labelIds: [],
  links: {},
  foodDietary: [],
  foodAllergies: [],
  foodFavorites: [],
  foodNotes: '',
}

export default function ContactFormPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const { data: contact, isLoading: isLoadingContact } = useContact(id)
  const { data: circles } = useCircles()
  const { data: labels } = useLabels()
  const createLabel = useCreateLabel()
  const createContact = useCreateContact()
  const updateContact = useUpdateContact()

  // Relationship hooks
  const { data: relationships } = useContactRelationships(id)
  const createRelationship = useCreateContactRelationship()
  const updateRelationship = useUpdateContactRelationship()
  const deleteRelationship = useDeleteContactRelationship()

  const [form, setForm] = useState<FormState>(INITIAL_STATE)
  const [hasPopulated, setHasPopulated] = useState(false)
  const [showNewLabel, setShowNewLabel] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')

  // Relationship state
  const [relationshipSearch, setRelationshipSearch] = useState('')
  const [showRelationshipSearch, setShowRelationshipSearch] = useState(false)
  const [editingRelId, setEditingRelId] = useState<string | null>(null)
  const relationshipSearchRef = useRef<HTMLDivElement>(null)
  const { data: relationshipSearchResults } = useContacts(
    relationshipSearch.length >= 2 ? { q: relationshipSearch, limit: 10 } : {},
  )

  // Avatar upload state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)

  useEffect(() => {
    if (isEditMode && contact && !hasPopulated) {
      setForm({
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        nickname: contact.nickname ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        company: contact.company ?? '',
        jobTitle: contact.jobTitle ?? '',
        location: contact.location ?? '',
        birthday: contact.birthday ?? '',
        anniversary: contact.anniversary ?? '',
        notes: contact.notes ?? '',
        contactFrequency: (contact.contactFrequency as ContactFrequency) ?? 'none',
        circleIds: ((contact as unknown as Record<string, unknown>).circleIds as string[]) ?? [],
        labelIds: ((contact as unknown as Record<string, unknown>).labelIds as string[]) ?? [],
        links: contact.links ?? {},
        foodDietary: contact.foodPreferences?.dietary ?? [],
        foodAllergies: contact.foodPreferences?.allergies ?? [],
        foodFavorites: contact.foodPreferences?.favorites ?? [],
        foodNotes: contact.foodPreferences?.notes ?? '',
      })
      setAvatarUrl(contact.avatarUrl ?? null)
      setAvatarPreview(contact.avatarUrl ?? null)
      setHasPopulated(true)
    }
  }, [isEditMode, contact, hasPopulated])

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleChip(key: 'circleIds' | 'labelIds', chipId: string) {
    setForm((prev) => {
      const current = prev[key]
      const next = current.includes(chipId)
        ? current.filter((v) => v !== chipId)
        : [...current, chipId]
      return { ...prev, [key]: next }
    })
  }

  async function handleAvatarSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset file input so re-selecting the same file works
    e.target.value = ''

    setAvatarError(null)

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError(t('contactForm.avatarInvalidType', 'Please select a JPEG, PNG, or WebP image'))
      return
    }

    if (file.size > MAX_AVATAR_SIZE) {
      setAvatarError(t('contactForm.avatarTooLarge', 'Image must be under 10MB'))
      return
    }

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file)
    setAvatarPreview(localPreview)
    setIsUploadingAvatar(true)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', 'avatar')

      const res = await fetch('/api/storage/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!res.ok) {
        throw new Error('Upload failed')
      }

      const { url } = await res.json()
      setAvatarUrl(url)
    } catch {
      setAvatarError(
        t('contactForm.avatarUploadFailed', 'Failed to upload image. Please try again.'),
      )
      setAvatarPreview(avatarUrl) // Revert to previous avatar
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  function handleRemoveAvatar() {
    setAvatarPreview(null)
    setAvatarUrl(null)
    setAvatarError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.firstName.trim()) return

    const payload: Record<string, unknown> = {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim() || null,
      nickname: form.nickname.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      avatar_url: avatarUrl,
      company: form.company.trim() || null,
      job_title: form.jobTitle.trim() || null,
      location: form.location.trim() || null,
      birthday: form.birthday || null,
      anniversary: form.anniversary || null,
      notes: form.notes.trim() || null,
      contact_frequency: form.contactFrequency,
      circle_ids: form.circleIds,
      label_ids: form.labelIds,
      links: Object.fromEntries(Object.entries(form.links).filter(([, v]) => v.trim())),
      food_preferences:
        form.foodDietary.length ||
        form.foodAllergies.length ||
        form.foodFavorites.length ||
        form.foodNotes.trim()
          ? {
              dietary: form.foodDietary,
              allergies: form.foodAllergies,
              favorites: form.foodFavorites,
              notes: form.foodNotes.trim(),
            }
          : null,
    }

    if (isEditMode && id) {
      await updateContact.mutateAsync({ id, ...payload })
      navigate(`/contacts/${id}`, { replace: true })
    } else {
      const created = await createContact.mutateAsync(payload)
      navigate(`/contacts/${created.id}`, { replace: true })
    }
  }

  function renderLabelChip(
    label: { id: string; name: string; color: string | null },
    isChild: boolean,
  ) {
    const selected = form.labelIds.includes(label.id)
    const color = label.color ?? undefined
    return (
      <button
        key={label.id}
        type="button"
        onClick={() => toggleChip('labelIds', label.id)}
        className={cn(
          'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
          selected ? 'text-white' : 'border-border bg-background text-foreground hover:bg-accent',
          isChild && !selected && 'text-xs',
        )}
        style={
          selected && color
            ? { backgroundColor: color, borderColor: color }
            : selected
              ? {
                  backgroundColor: 'hsl(var(--primary))',
                  borderColor: 'hsl(var(--primary))',
                }
              : undefined
        }
      >
        {label.name}
      </button>
    )
  }

  const isSaving = createContact.isPending || updateContact.isPending
  const isSubmitDisabled = isSaving || isUploadingAvatar || !form.firstName.trim()

  if (isEditMode && isLoadingContact) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-on-surface-variant h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
      {/* Header */}
      <header className="border-outline-variant/20 bg-background sticky top-0 z-10 flex items-center gap-3 border-b px-6 py-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-full"
          aria-label={t('contactForm.back', 'Back')}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="font-heading text-lg font-semibold">
          {isEditMode
            ? t('contactForm.editTitle', 'Edit Contact')
            : t('contactForm.newTitle', 'New Contact')}
        </h1>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg space-y-6 px-4 py-6">
          {/* Avatar upload */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAvatar}
                className="bg-surface-container-high relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full transition-opacity hover:opacity-80"
                aria-label={t('contactForm.changeAvatar', 'Change avatar')}
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Camera className="text-on-surface-variant h-8 w-8" />
                )}
                {isUploadingAvatar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
              </button>
              {avatarPreview && !isUploadingAvatar && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="bg-destructive text-destructive-foreground absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full shadow-sm"
                  aria-label={t('contactForm.removeAvatar', 'Remove avatar')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleAvatarSelect}
              className="hidden"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingAvatar}
              className="text-primary text-sm hover:underline"
            >
              {avatarPreview
                ? t('contactForm.changePhoto', 'Change photo')
                : t('contactForm.addPhoto', 'Add photo')}
            </button>
            {avatarError && <p className="text-destructive text-sm">{avatarError}</p>}
          </div>

          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="firstName" className="text-sm font-medium">
                {t('contactForm.firstName', 'First name')} *
              </label>
              <input
                id="firstName"
                type="text"
                required
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.firstNamePlaceholder', 'First name')}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="lastName" className="text-sm font-medium">
                {t('contactForm.lastName', 'Last name')}
              </label>
              <input
                id="lastName"
                type="text"
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.lastNamePlaceholder', 'Last name')}
              />
            </div>
          </div>

          {/* Nickname */}
          <div className="space-y-1.5">
            <label htmlFor="nickname" className="text-sm font-medium">
              {t('contactForm.nickname', 'Nickname')}
            </label>
            <input
              id="nickname"
              type="text"
              value={form.nickname}
              onChange={(e) => updateField('nickname', e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              placeholder={t('contactForm.nicknamePlaceholder', 'Nickname')}
            />
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                {t('contactForm.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.emailPlaceholder', 'email@example.com')}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="phone" className="text-sm font-medium">
                {t('contactForm.phone', 'Phone')}
              </label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.phonePlaceholder', '+1 (555) 000-0000')}
              />
            </div>
          </div>

          {/* Company & Job title */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="company" className="text-sm font-medium">
                {t('contactForm.company', 'Company')}
              </label>
              <input
                id="company"
                type="text"
                value={form.company}
                onChange={(e) => updateField('company', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.companyPlaceholder', 'Company')}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="jobTitle" className="text-sm font-medium">
                {t('contactForm.jobTitle', 'Job title')}
              </label>
              <input
                id="jobTitle"
                type="text"
                value={form.jobTitle}
                onChange={(e) => updateField('jobTitle', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t('contactForm.jobTitlePlaceholder', 'Job title')}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-1.5">
            <label htmlFor="location" className="text-sm font-medium">
              {t('contactForm.location', 'Location')}
            </label>
            <PlaceAutocomplete
              id="location"
              value={form.location}
              onChange={(val) => updateField('location', val)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              placeholder={t('contactForm.locationPlaceholder', 'City, Country')}
            />
          </div>

          {/* Birthday & Anniversary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="birthday" className="text-sm font-medium">
                {t('contactForm.birthday', 'Birthday')}
              </label>
              <input
                id="birthday"
                type="date"
                value={form.birthday}
                onChange={(e) => updateField('birthday', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="anniversary" className="text-sm font-medium">
                {t('contactForm.anniversary', 'Anniversary')}
              </label>
              <input
                id="anniversary"
                type="date"
                value={form.anniversary}
                onChange={(e) => updateField('anniversary', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label htmlFor="notes" className="text-sm font-medium">
              {t('contactForm.notes', 'Notes')}
            </label>
            <textarea
              id="notes"
              rows={4}
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              placeholder={t('contactForm.notesPlaceholder', 'Add notes about this contact...')}
            />
          </div>

          {/* Food & Dietary Preferences */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <UtensilsCrossed className="text-on-surface-variant size-4" />
              <span className="text-sm font-medium">
                {t('contactForm.foodPreferences', 'Food & Dietary')}
              </span>
            </div>
            <FoodTagInput
              label={t('contactForm.dietaryRestrictions', 'Dietary restrictions')}
              placeholder={t('contactForm.dietaryPlaceholder', 'e.g. vegetarian, vegan, halal…')}
              values={form.foodDietary}
              onChange={(v) => updateField('foodDietary', v)}
              suggestions={[
                'Vegetarian',
                'Vegan',
                'Pescatarian',
                'Halal',
                'Kosher',
                'Gluten-free',
                'Dairy-free',
                'Keto',
                'Paleo',
              ]}
            />
            <FoodTagInput
              label={t('contactForm.allergies', 'Allergies')}
              placeholder={t('contactForm.allergiesPlaceholder', 'e.g. peanuts, shellfish…')}
              values={form.foodAllergies}
              onChange={(v) => updateField('foodAllergies', v)}
              suggestions={[
                'Peanuts',
                'Tree nuts',
                'Shellfish',
                'Fish',
                'Eggs',
                'Milk',
                'Soy',
                'Wheat',
                'Sesame',
              ]}
            />
            <FoodTagInput
              label={t('contactForm.favorites', 'Favorites')}
              placeholder={t('contactForm.favoritesPlaceholder', 'e.g. sushi, Italian, chocolate…')}
              values={form.foodFavorites}
              onChange={(v) => updateField('foodFavorites', v)}
              suggestions={[]}
            />
            <div className="space-y-1">
              <label htmlFor="food-notes" className="text-on-surface-variant text-xs">
                {t('contactForm.foodNotes', 'Food notes')}
              </label>
              <textarea
                id="food-notes"
                rows={2}
                value={form.foodNotes}
                onChange={(e) => updateField('foodNotes', e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                placeholder={t(
                  'contactForm.foodNotesPlaceholder',
                  'Preferred restaurants, wine preferences, etc.',
                )}
              />
            </div>
          </div>

          {/* Links */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <LinkIcon className="text-on-surface-variant size-4" />
              <span className="text-sm font-medium">{t('contactForm.links', 'Links')}</span>
            </div>
            {LINK_KEYS.map((key) => (
              <div key={key} className="space-y-1">
                <label htmlFor={`link-${key}`} className="text-on-surface-variant text-xs">
                  {LINK_LABELS[key]}
                </label>
                <input
                  id={`link-${key}`}
                  type="url"
                  value={form.links[key] ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      links: { ...prev.links, [key]: e.target.value },
                    }))
                  }
                  className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
                  placeholder={LINK_PLACEHOLDERS[key]}
                />
              </div>
            ))}
          </div>

          {/* Circles picker */}
          <div className="space-y-2">
            <span className="text-sm font-medium">{t('contactForm.circles', 'Circles')}</span>
            <div className="flex flex-wrap gap-2">
              {circles?.map((circle) => {
                const selected = form.circleIds.includes(circle.id)
                return (
                  <button
                    key={circle.id}
                    type="button"
                    onClick={() => toggleChip('circleIds', circle.id)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-foreground hover:bg-accent',
                    )}
                  >
                    {circle.name}
                  </button>
                )
              })}
              {circles?.length === 0 && (
                <p className="text-on-surface-variant text-sm">
                  {t('contactForm.noCircles', 'No circles created yet')}
                </p>
              )}
            </div>
          </div>

          {/* Labels picker */}
          <div className="space-y-2">
            <span className="text-sm font-medium">{t('contactForm.labels', 'Labels')}</span>
            <div className="flex flex-col gap-1">
              {(() => {
                if (!labels || labels.length === 0) {
                  if (!showNewLabel) {
                    return (
                      <p className="text-on-surface-variant text-sm">
                        {t('contactForm.noLabels', 'No labels created yet')}
                      </p>
                    )
                  }
                  return null
                }

                // Build tree for grouped display
                const rootLabels = labels.filter((l) => !l.parentId)
                const childMap = new Map<string, typeof labels>()
                for (const l of labels) {
                  if (l.parentId) {
                    const existing = childMap.get(l.parentId) ?? []
                    existing.push(l)
                    childMap.set(l.parentId, existing)
                  }
                }

                return rootLabels.map((parent) => {
                  const children = childMap.get(parent.id) ?? []
                  return (
                    <div key={parent.id} className="flex flex-wrap items-start gap-1.5">
                      {renderLabelChip(parent, false)}
                      {children.map((child) => renderLabelChip(child, true))}
                    </div>
                  )
                })
              })()}
              {/* Inline create new label */}
              <div className="mt-1 flex flex-wrap gap-2">
                {showNewLabel ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      placeholder={t('contactForm.newLabelPlaceholder', 'Label name...')}
                      className="border-outline-variant/30 bg-surface-container focus:ring-ring rounded-full border px-3 py-1.5 text-sm outline-none focus:ring-2"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          const name = newLabelName.trim()
                          if (!name) return
                          createLabel.mutate(
                            { name },
                            {
                              onSuccess: (label) => {
                                toggleChip('labelIds', label.id)
                                setNewLabelName('')
                                setShowNewLabel(false)
                              },
                            },
                          )
                        }
                        if (e.key === 'Escape') {
                          setNewLabelName('')
                          setShowNewLabel(false)
                        }
                      }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setNewLabelName('')
                        setShowNewLabel(false)
                      }}
                      className="text-on-surface-variant hover:bg-accent rounded-full p-1"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowNewLabel(true)}
                    className="border-border text-on-surface-variant hover:bg-accent rounded-full border border-dashed px-3 py-1.5 text-sm transition-colors"
                  >
                    + {t('contactForm.newLabel', 'New label')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Relationships */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="text-on-surface-variant size-4" />
              <span className="text-sm font-medium">
                {t('contactForm.relationships', 'Relationships')}
              </span>
            </div>

            {!isEditMode ? (
              <p className="text-on-surface-variant text-sm">
                {t(
                  'contactForm.relationshipsSaveFirst',
                  'Save this contact first to add relationships.',
                )}
              </p>
            ) : (
              <>
                {/* Existing relationships */}
                {relationships && relationships.length > 0 && (
                  <div className="space-y-2">
                    {relationships.map((rel) => {
                      const isEditing = editingRelId === rel.id
                      return (
                        <div
                          key={rel.id}
                          className="border-outline-variant/30 rounded-lg border p-3"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                              style={{
                                backgroundColor:
                                  RELATIONSHIP_COLOR_MAP[rel.relationshipType] ?? '#94a3b8',
                              }}
                            >
                              {rel.relatedAvatarUrl ? (
                                <img
                                  src={rel.relatedAvatarUrl}
                                  alt=""
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                `${rel.relatedFirstName.charAt(0)}${rel.relatedLastName?.charAt(0) ?? ''}`.toUpperCase()
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">
                                {rel.relatedFirstName} {rel.relatedLastName}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                {RELATIONSHIP_TYPES.find((rt) => rt.value === rel.relationshipType)
                                  ?.label ?? rel.relationshipType}
                                {rel.strength > 1 && ` · ${rel.strength}/5`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingRelId(isEditing ? null : rel.id)}
                              className="text-on-surface-variant hover:text-foreground text-xs underline"
                            >
                              {isEditing ? t('common.close', 'Close') : t('common.edit', 'Edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteRelationship.mutate(rel.id)}
                              className="text-on-surface-variant hover:text-destructive p-1"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>

                          {/* Inline edit panel */}
                          {isEditing && (
                            <div className="mt-3 space-y-2 border-t pt-3">
                              <div>
                                <label className="text-on-surface-variant text-xs">
                                  {t('contactForm.relationshipType', 'Type')}
                                </label>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {RELATIONSHIP_TYPES.map((rt) => (
                                    <button
                                      key={rt.value}
                                      type="button"
                                      onClick={() =>
                                        updateRelationship.mutate({
                                          id: rel.id,
                                          relationship_type: rt.value,
                                        })
                                      }
                                      className={cn(
                                        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                                        rel.relationshipType === rt.value
                                          ? 'text-white'
                                          : 'border-border bg-background text-foreground hover:opacity-80',
                                      )}
                                      style={
                                        rel.relationshipType === rt.value
                                          ? {
                                              backgroundColor: rt.color,
                                              borderColor: rt.color,
                                            }
                                          : undefined
                                      }
                                    >
                                      {rt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="text-on-surface-variant text-xs">
                                  {t('contactForm.relationshipStrength', 'Strength')}
                                </label>
                                <div className="mt-1 flex gap-1">
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                      key={n}
                                      type="button"
                                      onClick={() =>
                                        updateRelationship.mutate({
                                          id: rel.id,
                                          strength: n,
                                        })
                                      }
                                      className="p-0.5"
                                    >
                                      <Star
                                        className={cn(
                                          'size-5 transition-colors',
                                          n <= rel.strength
                                            ? 'fill-amber-400 text-amber-400'
                                            : 'text-border',
                                        )}
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Add new relationship */}
                {showRelationshipSearch ? (
                  <div ref={relationshipSearchRef} className="space-y-2">
                    <div className="relative">
                      <Search className="text-on-surface-variant pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                      <input
                        type="text"
                        value={relationshipSearch}
                        onChange={(e) => setRelationshipSearch(e.target.value)}
                        className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-10 w-full rounded-md border py-2 pr-8 pl-9 text-sm focus-visible:ring-2 focus-visible:outline-none"
                        placeholder={t(
                          'contactForm.searchContactPlaceholder',
                          'Search contacts to link...',
                        )}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowRelationshipSearch(false)
                          setRelationshipSearch('')
                        }}
                        className="text-on-surface-variant hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 p-1"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                    {relationshipSearch.length >= 2 &&
                      relationshipSearchResults?.items &&
                      (() => {
                        const existingRelatedIds = new Set(
                          relationships?.map((r) => r.relatedContactId) ?? [],
                        )
                        const filtered = relationshipSearchResults.items.filter(
                          (c) => c.id !== id && !existingRelatedIds.has(c.id),
                        )
                        if (filtered.length === 0) {
                          return (
                            <p className="text-on-surface-variant px-1 text-sm">
                              {t('contactForm.noContactsFound', 'No contacts found')}
                            </p>
                          )
                        }
                        return (
                          <div className="border-outline-variant/30 max-h-48 overflow-y-auto rounded-md border">
                            {filtered.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                  createRelationship.mutate({
                                    contact_a_id: id!,
                                    contact_b_id: c.id,
                                  })
                                  setRelationshipSearch('')
                                  setShowRelationshipSearch(false)
                                }}
                                className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-left transition-colors"
                              >
                                <div className="bg-primary/10 text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                                  {c.avatarUrl ? (
                                    <img
                                      src={c.avatarUrl}
                                      alt=""
                                      className="h-7 w-7 rounded-full object-cover"
                                    />
                                  ) : (
                                    `${c.firstName.charAt(0)}${c.lastName?.charAt(0) ?? ''}`.toUpperCase()
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium">
                                    {c.firstName} {c.lastName}
                                  </p>
                                  {c.company && (
                                    <p className="text-muted-foreground truncate text-xs">
                                      {c.company}
                                    </p>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )
                      })()}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowRelationshipSearch(true)}
                    className="border-border text-on-surface-variant hover:bg-accent rounded-full border border-dashed px-3 py-1.5 text-sm transition-colors"
                  >
                    + {t('contactForm.addRelationship', 'Add relationship')}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Contact frequency */}
          <div className="space-y-2">
            <span className="text-sm font-medium">
              {t('contactForm.contactFrequency', 'Contact frequency')}
            </span>
            <div className="flex flex-wrap gap-2">
              {FREQUENCIES.map((freq) => {
                const selected = form.contactFrequency === freq.value
                return (
                  <button
                    key={freq.value}
                    type="button"
                    onClick={() => updateField('contactFrequency', freq.value)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-foreground hover:bg-accent',
                    )}
                  >
                    {t(`contactForm.freq.${freq.value}`, freq.label)}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky save button */}
      <div className="border-outline-variant/20 bg-background sticky bottom-0 border-t px-6 py-3">
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className={cn(
            'bg-primary text-primary-foreground flex h-12 w-full items-center justify-center rounded-xl text-sm font-semibold transition-opacity',
            isSubmitDisabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {isSaving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isEditMode ? (
            t('contactForm.save', 'Save')
          ) : (
            t('contactForm.create', 'Create Contact')
          )}
        </button>
      </div>
    </form>
  )
}

function FoodTagInput({
  label,
  placeholder,
  values,
  onChange,
  suggestions,
}: {
  label: string
  placeholder: string
  values: string[]
  onChange: (v: string[]) => void
  suggestions: string[]
}) {
  const [input, setInput] = useState('')
  const remaining = suggestions.filter(
    (s) => !values.some((v) => v.toLowerCase() === s.toLowerCase()),
  )

  function addTag(tag: string) {
    const trimmed = tag.trim()
    if (trimmed && !values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...values, trimmed])
    }
    setInput('')
  }

  return (
    <div className="space-y-1.5">
      <label className="text-on-surface-variant text-xs">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span
            key={v}
            className="bg-surface-container-high inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
          >
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-on-surface-variant hover:text-foreground ml-0.5"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {remaining.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="border-border text-on-surface-variant hover:bg-accent rounded-full border border-dashed px-2.5 py-0.5 text-xs transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            addTag(input)
          }
        }}
        className="border-outline-variant/30 bg-surface-container ring-offset-background placeholder:text-on-surface-variant focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:outline-none"
        placeholder={placeholder}
      />
    </div>
  )
}
